/**
 * <greeting-card-bridge> custom element.
 * Stores "armed" greeting card state and chains a cart add when the main
 * product is added via CartAddEvent.
 */
import { CartAddEvent } from "@theme/events";

const STORAGE_KEY = "gc-armed";
let _claimedUuid = null;

class GreetingCardBridge extends HTMLElement {
  _armed = null;
  #cartAddHandler = null;
  #saveHandler = null;
  #removeHandler = null;

  connectedCallback() {
    this.#restoreFromStorage();
    this.#saveHandler = (e) => this.#onSave(e);
    this.#removeHandler = () => this.#onRemove();
    this.#cartAddHandler = (e) => this.#onCartAdd(e);
    document.addEventListener("gc:save", this.#saveHandler);
    document.addEventListener("gc:remove", this.#removeHandler);
    document.addEventListener(CartAddEvent.eventName, this.#cartAddHandler);

    const removeBtn = this.querySelector("[data-gc-remove]");
    if (removeBtn) {
      removeBtn.addEventListener("click", () => {
        this.dispatchEvent(new CustomEvent("gc:remove", { bubbles: true }));
      });
    }

    const editBtn = this.querySelector("[data-gc-edit]");
    if (editBtn) {
      editBtn.addEventListener("click", () => this.#onEdit());
    }
  }

  disconnectedCallback() {
    if (this.#cartAddHandler) {
      document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
      this.#cartAddHandler = null;
    }
    if (this.#saveHandler) {
      document.removeEventListener("gc:save", this.#saveHandler);
      this.#saveHandler = null;
    }
    if (this.#removeHandler) {
      document.removeEventListener("gc:remove", this.#removeHandler);
      this.#removeHandler = null;
    }
  }

  #onSave(e) {
    const { variantId, designVariantId, message, uuid, designImage } = e.detail;
    this._armed = { variantId, designVariantId, message, uuid, designImage };
    _claimedUuid = null;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._armed)); } catch { /* storage unavailable */ }
    this.#updateUI();
  }

  #onRemove() {
    this._armed = null;
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* storage unavailable */ }
    this.#updateUI();
  }

  #onEdit() {
    if (!this._armed) return;
    const drawer = document.querySelector("greeting-card-drawer");
    if (drawer?.restoreState) {
      drawer.restoreState({
        variantId: this._armed.variantId,
        message: this._armed.message,
      });
    }
  }

  #restoreFromStorage() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        this._armed = JSON.parse(stored);
        this.#updateUI();
      }
    } catch { /* corrupted storage — ignore */ }
  }

  #updateUI() {
    const defaultView = this.querySelector("[data-gc-default-view]");
    const armedView = this.querySelector("[data-gc-armed-view]");
    if (!defaultView || !armedView) return;

    if (this._armed) {
      defaultView.hidden = true;
      armedView.hidden = false;
      const thumb = this.querySelector("[data-gc-armed-thumb]");
      const msgEl = this.querySelector("[data-gc-armed-message]");
      const priceEl = this.querySelector("[data-gc-armed-price]");
      if (thumb) thumb.src = this._armed.designImage || "";
      if (msgEl) {
        const preview = this._armed.message || "";
        msgEl.textContent = preview;
      }
      if (priceEl) {
        priceEl.textContent = this.#lookupPrice(this._armed.variantId) || "";
      }
    } else {
      defaultView.hidden = false;
      armedView.hidden = true;
    }
  }

  async #onCartAdd(e) {
    if (!this._armed) return;
    if (e.detail?.data?.source === "greeting-card-bridge") return;
    if (this._armed.uuid === _claimedUuid) return;
    _claimedUuid = this._armed.uuid;

    const consumedUuid = this._armed.uuid;
    const parentVariantId = this.#getParentVariantId();
    const cartAddUrl = window.Theme?.routes?.cart_add_url || "/cart/add.js";

    const sectionIds = [...document.querySelectorAll("cart-items-component")]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const payload = {
      items: [
        {
          id: this._armed.variantId,
          quantity: 1,
          properties: {
            _greeting_card_id: this._armed.uuid,
            _greeting_card_design: String(this._armed.designVariantId),
            _greeting_message: this._armed.message,
            _parent_variant_id: parentVariantId,
            Message: this._armed.message,
          },
        },
      ],
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(",");
    }

    try {
      const response = await fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.status) {
        _claimedUuid = null;
        console.error("[greeting-card-bridge]", data.message || data.description || data);
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, "greeting-card-bridge", {
          source: "greeting-card-bridge",
          variantId: String(this._armed.variantId),
          sections: data.sections,
        }),
      );

      if (this._armed?.uuid === consumedUuid) {
        this.#onRemove();
      }
    } catch (err) {
      _claimedUuid = null;
      console.error("[greeting-card-bridge]", err);
    }
  }

  #lookupPrice(variantId) {
    const script = document.querySelector('greeting-card-drawer script[type="application/json"]');
    if (!script) return "";
    try {
      const data = JSON.parse(script.textContent);
      const match = data.variants?.find((v) => String(v.id) === String(variantId));
      return match?.price || "";
    } catch {
      return "";
    }
  }

  #getParentVariantId() {
    const form = document.querySelector("product-form-component form, form[data-type='add-to-cart-form']");
    const input = form?.querySelector('input[name="id"]');
    return input?.value || "";
  }
}

if (!customElements.get("greeting-card-bridge")) {
  customElements.define("greeting-card-bridge", GreetingCardBridge);
}
